import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Breadcrumbs } from '../../components/Breadcrumbs.js';
import {
  Banner,
  Button,
  Input,
  Modal,
  Select,
  Skeleton,
  Table,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  type ColumnDef,
  type RowSelectionState,
  type SortingState,
} from '@servicecompressionllc/sc-design-system';
import { AppShell } from '../../components/AppShell.js';
import { Pagination } from '../../components/Pagination.js';
import { ScopeSelector } from '../../components/ScopeSelector.js';
import { useMe } from '../../hooks/useMe.js';
import type { GroupDetail, GroupMember } from '../../lib/types.js';
import { useScopesLogic } from '../scopes/useScopesLogic.js';
import { useUsersBulkMembershipLogic } from '../users/useUsersBulkMembershipLogic.js';
import { useUsersListLogic } from '../users/useUsersListLogic.js';
import {
  useGroupDetailLogic,
  useRenameGroupLogic,
  useDeleteGroupLogic,
  useSetGroupScopesLogic,
} from './useGroupsLogic.js';

const PAGE_SIZE = 25;

const formatDate = (iso: string): string => new Date(iso).toLocaleDateString();

const TabButton = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
      active ? 'border-primary text-ink' : 'border-transparent text-ink-muted hover:text-ink'
    }`}
  >
    {children}
  </button>
);

const ConfirmDeleteModal = ({
  name,
  onConfirm,
  onCancel,
  isDeleting,
}: {
  name: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting: boolean;
}) => (
  <Modal open onOpenChange={onCancel}>
    <Modal.Header>
      <Modal.Title>Delete group</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <p className="text-sm text-ink-muted">
        Delete the group "{name}"? Members lose the scopes this group granted them. This can't be
        undone.
      </p>
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={onCancel} disabled={isDeleting}>
        Cancel
      </Button>
      <Button variant="danger" onClick={onConfirm} disabled={isDeleting}>
        {isDeleting ? 'Deleting…' : 'Delete'}
      </Button>
    </Modal.Footer>
  </Modal>
);

/** Inline scope editor (replaces the old modal). */
const ScopesSection = ({ tenantId, detail }: { tenantId: string; detail: GroupDetail }) => {
  const { scopes: catalog, isLoading: scopesLoading } = useScopesLogic();
  const scopesLogic = useSetGroupScopesLogic(tenantId);
  const [selected, setSelected] = useState<string[]>(detail.scopes);
  const [saved, setSaved] = useState(false);
  const dirty = useMemo(() => {
    const a = [...detail.scopes].sort().join(',');
    const b = [...selected].sort().join(',');
    return a !== b;
  }, [detail.scopes, selected]);

  const save = async () => {
    try {
      await scopesLogic.setScopes(detail.id, detail.scopes, selected);
      setSaved(true);
    } catch {
      // surfaced via banner
    }
  };

  return (
    <section className="bg-surface-elevated rounded-lg border border-outline p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="text-ink-muted text-sm">Capabilities every member of this group inherits.</p>
        <Button onClick={() => void save()} disabled={scopesLogic.isSubmitting || !dirty}>
          {scopesLogic.isSubmitting ? 'Saving…' : 'Save scopes'}
        </Button>
      </div>
      {scopesLogic.error ? (
        <Banner variant="danger" className="mb-3">
          <Banner.Body>
            <Banner.Title>{scopesLogic.error.message}</Banner.Title>
          </Banner.Body>
        </Banner>
      ) : (
        saved &&
        !dirty && (
          <Banner variant="success" className="mb-3">
            <Banner.Body>
              <Banner.Title>Scopes saved.</Banner.Title>
            </Banner.Body>
          </Banner>
        )
      )}
      <ScopeSelector
        audience="user"
        scopes={catalog}
        selectedScopes={selected}
        onSelectedScopesChange={(next) => {
          setSelected(next);
          setSaved(false);
        }}
        isLoading={scopesLoading}
        isDisabled={scopesLogic.isSubmitting}
        fill
      />
    </section>
  );
};

/** Members table — bulk-select and remove from the group. */
const MembersSection = ({ tenantId, detail }: { tenantId: string; detail: GroupDetail }) => {
  const bulk = useUsersBulkMembershipLogic(tenantId);
  const allUsers = useUsersListLogic(tenantId);
  const [addPick, setAddPick] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [notice, setNotice] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q === '') return detail.members;
    return detail.members.filter(
      (m) => m.email.toLowerCase().includes(q) || (m.name ?? '').toLowerCase().includes(q),
    );
  }, [detail.members, search]);

  const columns = useMemo<ColumnDef<GroupMember>[]>(
    () => [
      {
        id: 'select',
        enableSorting: false,
        header: ({ table }) => (
          <input
            type="checkbox"
            className="accent-primary"
            aria-label="Select all on this page"
            checked={table.getIsAllPageRowsSelected()}
            ref={(el) => {
              if (el) el.indeterminate = table.getIsSomePageRowsSelected();
            }}
            onChange={table.getToggleAllPageRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            className="accent-primary"
            aria-label={`Select ${row.original.email}`}
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
        cell: (info) => <span className="text-ink">{info.getValue<string>()}</span>,
      },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: (info) => (
          <span className="text-ink-muted">{info.getValue<string | null>() ?? '—'}</span>
        ),
      },
      {
        accessorKey: 'addedAt',
        header: 'Added',
        cell: (info) => (
          <span className="text-ink-muted text-xs">{formatDate(info.getValue<string>())}</span>
        ),
      },
    ],
    [],
  );

  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting, rowSelection },
    getRowId: (m) => m.userId,
    enableRowSelection: true,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: PAGE_SIZE } },
  });

  const selectedIds = Object.keys(rowSelection).filter((id) => rowSelection[id]);

  const removeSelected = async () => {
    if (selectedIds.length === 0) return;
    try {
      await bulk.apply({ groupId: detail.id, add: [], remove: selectedIds });
      setNotice(
        `Removed ${selectedIds.length} ${selectedIds.length === 1 ? 'user' : 'users'} from ${detail.name}.`,
      );
      setRowSelection({});
    } catch {
      // surfaced via bulk.error
    }
  };

  // Users not already in this group — the add picker's options.
  const memberIds = new Set(detail.members.map((m) => m.userId));
  const addableOptions = (allUsers.data?.users ?? [])
    .filter((u) => !memberIds.has(u.id))
    .map((u) => ({ value: u.id, label: u.name ? `${u.name} (${u.email})` : u.email }));
  const addPickId = addPick[0];

  const addMember = async () => {
    if (!addPickId) return;
    const label = addableOptions.find((o) => o.value === addPickId)?.label ?? 'user';
    try {
      await bulk.apply({ groupId: detail.id, add: [addPickId], remove: [] });
      setNotice(`Added ${label} to ${detail.name}.`);
      setAddPick([]);
    } catch {
      // surfaced via bulk.error
    }
  };

  const pageIndex = table.getState().pagination.pageIndex;
  const pageCount = table.getPageCount();

  return (
    <section className="bg-surface-elevated rounded-lg border border-outline p-4">
      <p className="text-ink-muted mb-3 text-sm">
        Add a user to this group, or select members below to remove them in bulk.
      </p>

      <div className="mb-3 flex items-center gap-2">
        <Select
          options={addableOptions}
          value={addPick}
          onChange={setAddPick}
          placeholder={addableOptions.length === 0 ? 'Everyone is a member' : 'Add a user…'}
          aria-label="Add a user to this group"
          disabled={addableOptions.length === 0 || bulk.isSubmitting}
          className="min-w-72"
        />
        <Button
          size="sm"
          onClick={() => void addMember()}
          disabled={!addPickId || bulk.isSubmitting}
        >
          {bulk.isSubmitting ? 'Adding…' : 'Add'}
        </Button>
      </div>

      {notice && (
        <Banner variant="success" className="mb-3">
          <Banner.Body>
            <Banner.Title>{notice}</Banner.Title>
          </Banner.Body>
        </Banner>
      )}
      {bulk.error && (
        <Banner variant="danger" className="mb-3">
          <Banner.Body>
            <Banner.Title>{bulk.error.message}</Banner.Title>
          </Banner.Body>
        </Banner>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Input
          type="search"
          placeholder="Search email or name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
          aria-label="Search members"
        />
        {selectedIds.length > 0 && (
          <div className="ml-auto flex items-center gap-2">
            <span className="text-sm text-ink-muted">{selectedIds.length} selected</span>
            <Button
              size="sm"
              variant="danger"
              onClick={() => void removeSelected()}
              disabled={bulk.isSubmitting}
            >
              Remove from group ({selectedIds.length})
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setRowSelection({})}>
              Clear
            </Button>
          </div>
        )}
        {selectedIds.length === 0 && (
          <span className="ml-auto text-xs text-ink-muted">
            {detail.members.length} {detail.members.length === 1 ? 'member' : 'members'}
          </span>
        )}
      </div>

      <div className="max-h-[calc(100vh-26rem)] overflow-auto rounded-lg border border-outline">
        <Table.Root className="w-full min-w-[40rem] text-sm">
          <Table.Header>
            {table.getHeaderGroups().map((hg) => (
              <Table.Row key={hg.id}>
                {hg.headers.map((header) => (
                  <Table.HeaderCell key={header.id} className="whitespace-nowrap">
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </Table.HeaderCell>
                ))}
              </Table.Row>
            ))}
          </Table.Header>
          <Table.Body>
            {table.getRowModel().rows.map((row) => (
              <Table.Row key={row.id} selected={row.getIsSelected()}>
                {row.getVisibleCells().map((cell) => (
                  <Table.Cell key={cell.id} className="whitespace-nowrap">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
        {detail.members.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-ink-muted">
            No members yet — add one with the picker above.
          </p>
        ) : (
          filtered.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-ink-muted">
              No members match your search.
            </p>
          )
        )}
      </div>

      <Pagination
        className="mt-3"
        page={pageIndex}
        pageCount={pageCount}
        onPageChange={(p) => table.setPageIndex(p)}
      />
    </section>
  );
};

const GroupManageBody = ({ tenantId, detail }: { tenantId: string; detail: GroupDetail }) => {
  const navigate = useNavigate();
  const renameLogic = useRenameGroupLogic(tenantId);
  const deleteLogic = useDeleteGroupLogic(tenantId);
  const [name, setName] = useState(detail.name);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [tab, setTab] = useState<'members' | 'scopes'>('members');
  const nameChanged = name.trim().length > 0 && name.trim() !== detail.name;

  const rename = async () => {
    if (!nameChanged) return;
    try {
      await renameLogic.rename(detail.id, name);
    } catch {
      // surfaced below
    }
  };

  const remove = async () => {
    try {
      await deleteLogic.delete(detail.id);
      navigate('/settings/groups');
    } catch {
      setConfirmDelete(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <label htmlFor="group-name" className="text-ink mb-1 block text-sm font-medium">
            Group name
          </label>
          <div className="flex max-w-md gap-2">
            <Input
              id="group-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={renameLogic.isSubmitting}
            />
            <Button
              variant="secondary"
              onClick={() => void rename()}
              disabled={!nameChanged || renameLogic.isSubmitting}
            >
              {renameLogic.isSubmitting ? 'Saving…' : 'Rename'}
            </Button>
          </div>
          {renameLogic.error && (
            <p className="text-danger mt-1 text-sm">{renameLogic.error.message}</p>
          )}
        </div>
        <Button variant="danger" onClick={() => setConfirmDelete(true)}>
          Delete group
        </Button>
      </div>

      <div className="flex gap-1 border-b border-outline">
        <TabButton active={tab === 'members'} onClick={() => setTab('members')}>
          Members
        </TabButton>
        <TabButton active={tab === 'scopes'} onClick={() => setTab('scopes')}>
          Scopes
        </TabButton>
      </div>

      {tab === 'members' ? (
        <MembersSection tenantId={tenantId} detail={detail} />
      ) : (
        <ScopesSection tenantId={tenantId} detail={detail} />
      )}

      {confirmDelete && (
        <ConfirmDeleteModal
          name={detail.name}
          onConfirm={() => void remove()}
          onCancel={() => setConfirmDelete(false)}
          isDeleting={deleteLogic.isSubmitting}
        />
      )}
    </div>
  );
};

export const GroupManagePage = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const meQuery = useMe();
  const tenantId = meQuery.data?.tenant.id;
  const { data: detail, isLoading, error } = useGroupDetailLogic(tenantId, groupId);

  return (
    <AppShell>
      <Breadcrumbs trail={[{ label: 'Groups', to: '/settings/groups' }]} />

      {error && (
        <Banner variant="danger" className="mb-4">
          <Banner.Body>
            <Banner.Title>{error.message}</Banner.Title>
          </Banner.Body>
        </Banner>
      )}

      {(meQuery.isLoading || isLoading || !detail || !tenantId) && !error ? (
        <div className="space-y-4" role="status" aria-label="Loading group">
          <Skeleton className="h-9 w-64" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : (
        detail &&
        tenantId && (
          <>
            <h2 className="text-ink mb-4 text-2xl font-bold">{detail.name}</h2>
            <GroupManageBody tenantId={tenantId} detail={detail} />
          </>
        )
      )}
    </AppShell>
  );
};
